import React, { ReactNode, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';

import ErrorBoundary from '@/components/ui/error-boundary';
import GlobalSheetRenderer from '@/components/ui/global-sheet-renderer';
import { Toaster } from '@/components/ui/sonner';
import {
  initializeErrorReporting,
  createDefaultErrorReportingConfig,
} from '@/services/error-reporting';

import { ApiProvider } from './ApiContext';
import { AuthProvider } from './AuthContext';
import { GlobalStateProvider } from './GlobalStateContext';
import { NotificationProvider } from './NotificationContext';
import { ThemeProvider } from './ThemeContext';
import { ToastProvider } from './ToastContext';

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * AppProviders component that wraps the application with all necessary context providers
 *
 * Provider order is important:
 * 1. Router - provides routing context
 * 2. ErrorBoundary - catches and handles errors
 * 3. ToastProvider - provides toast context
 * 4. AuthProvider - provides authentication context
 * 5. ApiProvider - provides API context (depends on auth)
 * 6. NotificationProvider - provides notification context (depends on API)
 * 7. GlobalStateProvider - provides global state context (can use all other contexts)
 */
const AppProviders: React.FC<AppProvidersProps> = ({ children }) => {
  useEffect(() => {
    // Initialize error reporting once on app mount
    initializeErrorReporting(createDefaultErrorReportingConfig());
  }, []);

  return (
    <Router
      basename={(() => {
        const base = import.meta.env.BASE_URL || '/';
        try {
          const path = new URL(base, window.location.origin).pathname;
          return path === '/./' || path === './' ? '/' : path || '/';
        } catch {
          return '/';
        }
      })()}
    >
      <ErrorBoundary>
        <ThemeProvider>
          <ToastProvider>
            <AuthProvider>
              <ApiProvider>
                <NotificationProvider>
                  <GlobalStateProvider>
                    {children}
                    <Toaster />
                    <GlobalSheetRenderer />
                  </GlobalStateProvider>
                </NotificationProvider>
              </ApiProvider>
            </AuthProvider>
          </ToastProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </Router>
  );
};

export default AppProviders;

// Re-export all context types for easy importing
export type {
  ApiContextType,
  ApiError,
  ApiResponse,
  RequestConfig,
} from './ApiContext';
export type { AuthContextType, User } from './AuthContext';
export type {
  GlobalAction,
  GlobalLoading,
  GlobalSheet,
  GlobalState,
  GlobalStateContextType,
} from './GlobalStateContext';
export type {
  NotificationContextType,
  NotificationItem,
  ToastConfig as NotificationToastConfig,
} from './NotificationContext';
export type {
  ThemeContextType,
  ThemeMode,
  ThemePreferences,
} from './ThemeContext';
export type { ToastConfig, ToastContextType } from './ToastContext';
